import { Grid } from '@mui/material';
import React, { useMemo } from 'react';
import { FormikValues, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import TextInput from '../components/inputs/textInput';
import EmailInput from '../components/inputs/emailInput';
import { usersFormValidationSchema } from '../validations/usersFormValidationSchema';
import { ManagedOrganizationInterface, UserInterface } from '../interfaces/user.interface';
import { useUpdateUser } from '../apiHooks/mutations/userMutations/userMutations';
import SecondaryButton from '../components/buttons/secondaryButton';
import PrimaryButton from '../components/buttons/primaryButton';
import OrganizationSelect from '../components/dropdownSelects/organizationSelect';
import useGetManagedOrganizationsNames from '../apiHooks/queries/organizationManagedNames/getOrganizationsManagedNames';
import { useOrganizationSettingsStore } from '../store/organizationSettingsStore';

interface UsersFormInterface {
    closeDialog: () => void;
    data?: UserInterface;
    handleAfterCreate?: (newUserId: string) => void;
}

const UsersForm: React.FC<UsersFormInterface> = (props) => {
  const { mutateAsync: updateUser, isLoading: isUpdatingUser } = useUpdateUser();
  const { t } = useTranslation();

  const organizationSettings = useOrganizationSettingsStore((state) => state.organizationSettings);
  const isManager = Boolean(organizationSettings?.managerialStatus == 2);
  const defaultOrganizationId = organizationSettings?.organizationId
    ? String(organizationSettings.organizationId)
    : '';

  const formik: FormikValues = useFormik<UserInterface>({
    initialValues: {
      id: props.data?.id,
      firstName: props.data?.firstName || '',
      lastName: props.data?.lastName || '',
      email: props.data?.email || '',
      password: '',
      organizations: props.data?.organizations || null,
      organizationId: props.data?.organizations?.orgId || defaultOrganizationId,
    },
    enableReinitialize: true,
    validationSchema: () => usersFormValidationSchema(t),
    onSubmit: async (values) => {
      const payload: UserInterface = {
        ...values,
        organizationId: isManager
          ? values.organizationId
          : (values.organizationId || defaultOrganizationId),
      };
      if (values.id) {
        await updateUser(payload);
        props.closeDialog();
      } else {
        const response = await updateUser(payload);
        const newUserPassword = response?.data?.data[0];
        if (newUserPassword && props.handleAfterCreate) {
          props.handleAfterCreate(newUserPassword);
          props.closeDialog();
        }
      }
    },
  });
  const {
    data: managedOrganizationsResponse,
    isLoading: isManagedOrganizationsLoading,
  } = useGetManagedOrganizationsNames('userForm');

  const organizationOptions: ManagedOrganizationInterface[] = useMemo(() => {
    const managedOrganizations = managedOrganizationsResponse?.data?.data ?? [];
    const userOrganization = props.data?.organizations;

    const filteredManaged = userOrganization?.managerOrgId
      ? managedOrganizations.filter((org) => org.managerOrgId === userOrganization.managerOrgId)
      : managedOrganizations;

    const organizationsMap = new Map<string, ManagedOrganizationInterface>();
    filteredManaged.forEach((org) => {
      organizationsMap.set(org.orgId, org);
    });

    if (userOrganization) {
      organizationsMap.set(userOrganization.orgId, userOrganization);

      if (userOrganization.managerOrgId && userOrganization.managerOrgName) {
        organizationsMap.set(userOrganization.managerOrgId, {
          orgId: userOrganization.managerOrgId,
          orgName: userOrganization.managerOrgName,
          managerOrgId: userOrganization.managerOrgId,
          managerOrgName: userOrganization.managerOrgName,
        });
      }
    }

    if (organizationSettings?.name && organizationSettings?.organizationId) {
      const organizationIdString = String(organizationSettings.organizationId);
      organizationsMap.set(organizationIdString, {
        orgId: organizationIdString,
        orgName: organizationSettings.name,
        managerOrgId: organizationIdString,
        managerOrgName: organizationSettings.name,
      });
    }

    return Array.from(organizationsMap.values());
  }, [managedOrganizationsResponse?.data, props.data?.organizations, organizationSettings]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid
        container
        sx={{
          display: 'flex', flexDirection: 'column', columnGap: '12px', rowGap: '12px', paddingBlock: '1rem',
        }}
      >
        <Grid item xs={12} sm={15}>
          <TextInput
            myKey="firstName"
            name="firstName"
            value={formik.values.firstName}
            label={t('firstName')}
            placeholder={t('firstName')}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.firstName) && Boolean(formik.errors.firstName)}
            helperText={Boolean(formik.touched.firstName) && formik.errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={15}>
          <TextInput
            myKey="lastName"
            name="lastName"
            value={formik.values.lastName}
            label={t('lastName')}
            placeholder={t('lastName')}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.lastName) && Boolean(formik.errors.lastName)}
            helperText={Boolean(formik.touched.lastName) && formik.errors.lastName}
          />
        </Grid>
        <Grid item xs={12} sm={15}>
          <EmailInput
            id="email"
            myKey="email"
            name="email"
            value={formik.values.email}
            label={t('email')}
            placeholder={t('email')}
            fullWidth
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.email) && Boolean(formik.errors.email)}
            helperText={Boolean(formik.touched.email) && formik.errors.email}
            dataCy="user-editor-email"
          />
        </Grid>
        {isManager && (
            <Grid item xs={12} sm={15}>
              <OrganizationSelect
                  placeholder={t('organization')}
                  data={props.data?.organizations}
                  label={t('organization')}
                  formik={formik}
                  fullWidth
                  options={organizationOptions}
                  loading={isManagedOrganizationsLoading}
              />
            </Grid>
        )}

      </Grid>
      <div className="ml-auto mt-[48px] flex w-fit gap-3">
        <SecondaryButton type="button" onClick={props.closeDialog} dataCy="user-editor-cancel">
          {t('cancel')}
        </SecondaryButton>
        <PrimaryButton type="submit" disabled={isUpdatingUser} dataCy="user-editor-submit">
          {t('submit')}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default UsersForm;
